/**
 * Drawing other players.
 *
 * Meshes are created once per player and reused. Rebuilding them each frame
 * would be the single easiest way to make a busy town unplayable on a phone.
 */

import type { Scene } from '@babylonjs/core/scene.js';
import type { Camera } from '@babylonjs/core/Cameras/camera.js';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector.js';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';

import type { RemotePlayer } from '../net/room.js';
import { buildFigure, poseWalk, type FigureRig } from './figure.js';

interface RemoteVisual {
  root: TransformNode;
  nameplate: HTMLElement;
  /** Their limbs, so they walk rather than glide. */
  rig: FigureRig;
  /** Where they were last frame, for working out their speed. */
  last: { x: number; z: number };
}

export class RemoteRenderer {
  private readonly visuals = new Map<string, RemoteVisual>();
  private readonly scene: Scene;
  private readonly nameplateLayer: HTMLElement;

  constructor(scene: Scene, nameplateLayer: HTMLElement) {
    this.scene = scene;
    this.nameplateLayer = nameplateLayer;
  }

  private create(remote: RemotePlayer): RemoteVisual {
    const root = new TransformNode(`remote-${remote.sessionId}`, this.scene);

    /* The same builder the local player uses, so everybody in the world is
       the same kind of thing. Other players used to be capsules while you were
       a capsule too — now they are people, and their appearance is theirs
       rather than an approximation of it. */
    const rig = buildFigure(this.scene, remote.appearance, {
      name: `remote-${remote.sessionId}`
    });
    rig.root.parent = root;
    // Never blocks the local camera — see camera.ts.
    for (const mesh of rig.root.getChildMeshes()) mesh.isPickable = false;

    const nameplate = document.createElement('div');
    nameplate.className = 'nameplate';
    nameplate.textContent = `${remote.name} · ${remote.level}`;
    this.nameplateLayer.append(nameplate);

    return {
      root, nameplate, rig,
      last: { x: remote.render.x, z: remote.render.z }
    };
  }

  /**
   * Syncs meshes and nameplates to the current remote list.
   *
   * `dt` is seconds since the last frame — the walk cycle needs it, and taking
   * it as an argument keeps this class free of its own clock.
   */
  update(
    remotes: Map<string, RemotePlayer>,
    camera: Camera,
    canvas: HTMLCanvasElement,
    dt: number
  ): void {
    // Remove anyone who has left.
    for (const [sessionId, visual] of this.visuals) {
      if (!remotes.has(sessionId)) {
        visual.root.dispose(false, true);
        visual.nameplate.remove();
        this.visuals.delete(sessionId);
      }
    }

    const engine = this.scene.getEngine();
    const transform = this.scene.getTransformMatrix();
    const viewport = camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight());

    for (const [sessionId, remote] of remotes) {
      let visual = this.visuals.get(sessionId);
      if (visual === undefined) {
        visual = this.create(remote);
        this.visuals.set(sessionId, visual);
      }

      visual.root.position.set(remote.render.x, remote.render.y, remote.render.z);
      visual.root.rotation.y = remote.render.facing;

      /* Their speed comes from how far they actually moved between frames.
         There is no velocity in the state and there does not need to be — the
         interpolated position already carries it. */
      const moved = Math.hypot(
        remote.render.x - visual.last.x, remote.render.z - visual.last.z);
      visual.last.x = remote.render.x;
      visual.last.z = remote.render.z;
      poseWalk(visual.rig, dt > 0 ? moved / dt : 0, dt);

      // Project the head position into screen space for the nameplate.
      const head = new Vector3(remote.render.x, remote.render.y + 2.15, remote.render.z);
      // Identity for the world matrix: `head` is already a world-space point,
      // so there is no local-to-world transform still to apply.
      const projected = Vector3.Project(head, Matrix.Identity(), transform, viewport);

      // z outside 0..1 means behind the camera. Without this check the plate
      // reappears mirrored on the far side of the screen.
      const behind = projected.z < 0 || projected.z > 1;
      const distance = Vector3.Distance(camera.position, head);

      if (behind || distance > 60) {
        visual.nameplate.style.display = 'none';
      } else {
        visual.nameplate.style.display = '';
        // Canvas pixels to CSS pixels — they differ whenever the render scale
        // is not 1, which is most of the time on a phone.
        const scaleX = canvas.clientWidth / engine.getRenderWidth();
        const scaleY = canvas.clientHeight / engine.getRenderHeight();
        visual.nameplate.style.left = `${projected.x * scaleX}px`;
        visual.nameplate.style.top = `${projected.y * scaleY}px`;
        visual.nameplate.classList.toggle('away', remote.away);
        visual.nameplate.textContent = remote.away
          ? `${remote.name} (away)`
          : `${remote.name} · ${remote.level}`;
      }
    }
  }

  dispose(): void {
    for (const visual of this.visuals.values()) {
      visual.root.dispose(false, true);
      visual.nameplate.remove();
    }
    this.visuals.clear();
  }
}
